import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Package, Headphones, DollarSign, Pencil } from 'lucide-react';

const emptyForm = { name: '', type: 'post_purchase_upsell', offered_product_id: '', bundled_product_ids: [], price: '', headline: '', bullets: [], attached_to_product_id: '' };

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const load = () => {
    api.get('/offers').then(r => setOffers(r.data)).catch(() => {});
    api.get('/products').then(r => setProducts(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const toggleBundled = (productId) => {
    setForm(prev => {
      const ids = prev.bundled_product_ids.includes(productId)
        ? prev.bundled_product_ids.filter(id => id !== productId)
        : [...prev.bundled_product_ids, productId];
      return { ...prev, bundled_product_ids: ids };
    });
  };

  const bundledTotal = form.bundled_product_ids.reduce((sum, id) => {
    const p = products.find(pr => pr.id === id);
    return sum + (p ? parseFloat(p.price || 0) : 0);
  }, 0);

  const openCreate = () => {
    setEditingOffer(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (offer) => {
    setEditingOffer(offer);
    setForm({
      name: offer.name || '',
      type: offer.type || 'post_purchase_upsell',
      offered_product_id: offer.offered_product_id || '',
      bundled_product_ids: offer.bundled_product_ids || [],
      price: offer.price ?? '',
      headline: offer.headline || '',
      bullets: offer.bullets || [],
      attached_to_product_id: offer.attached_to_product_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.offered_product_id) {
      toast.error('Name and primary product are required');
      return;
    }
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editingOffer) {
        await api.put(`/offers/${editingOffer.id}`, payload);
        toast.success('Offer updated');
      } else {
        await api.post('/offers', payload);
        toast.success('Offer created');
      }
      setDialogOpen(false);
      setEditingOffer(null);
      setForm({ ...emptyForm });
      load();
    } catch { toast.error('Failed to save offer'); }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    await api.delete(`/offers/${id}`);
    load();
    toast.success('Deleted');
  };

  const getProductTitle = (id) => products.find(p => p.id === id)?.title || 'Unknown';
  const typeColor = { order_bump: 'bg-blue-500/10 text-blue-600', post_purchase_upsell: 'bg-[#1A9DA5]/10 text-[#1A9DA5]', cross_sell: 'bg-purple-500/10 text-purple-600' };

  return (
    <div className="space-y-6" data-testid="offers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Offers</h1>
          <p className="text-muted-foreground mt-1">{offers.length} offers</p>
        </div>
        <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white" onClick={openCreate} data-testid="new-offer-btn">
          <Plus className="w-4 h-4 mr-1" />New Offer
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {offers.map(o => {
          const bundled = o.bundled_product_ids || [];
          return (
            <Card key={o.id} data-testid={`offer-card-${o.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="outline" className={typeColor[o.type] || ''}>{o.type?.replace(/_/g, ' ')}</Badge>
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteOffer(o.id)} data-testid={`delete-offer-${o.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <h3 className="font-semibold text-sm font-['Outfit']">{o.name}</h3>
                {o.headline && <p className="text-xs text-muted-foreground mt-1">{o.headline}</p>}
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-muted-foreground">Primary: {getProductTitle(o.offered_product_id)}</p>
                  {bundled.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium">+{bundled.length} bundled:</span>
                      {bundled.map(bid => <span key={bid} className="ml-1">{getProductTitle(bid)},</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-lg font-bold font-['Outfit']">${o.price}</p>
                  {o.attached_to_product_id && (
                    <Badge variant="outline" className="text-xs">After: {getProductTitle(o.attached_to_product_id)}</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {offers.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center">
            <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No offers yet. Create your first upsell bundle!</p>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingOffer(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle>{editingOffer ? 'Edit Offer' : 'Create Upsell Offer'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Offer Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Business Starter Bundle" data-testid="offer-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order_bump">Order Bump</SelectItem>
                    <SelectItem value="post_purchase_upsell">Post-Purchase Upsell</SelectItem>
                    <SelectItem value="cross_sell">Cross-Sell</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Primary Product</Label>
                <Select value={form.offered_product_id} onValueChange={v => setForm({ ...form, offered_product_id: v })}>
                  <SelectTrigger data-testid="offer-primary-product"><SelectValue placeholder="Select primary product" /></SelectTrigger>
                  <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.title} (${p.price})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Show After Purchasing</Label>
                <Select value={form.attached_to_product_id} onValueChange={v => setForm({ ...form, attached_to_product_id: v })}>
                  <SelectTrigger data-testid="offer-attached-product"><SelectValue placeholder="Show after buying this product" /></SelectTrigger>
                  <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">This offer appears after the customer buys the selected product.</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Package className="w-4 h-4 text-[#E8735A]" />Bundle Products</Label>
              <p className="text-xs text-muted-foreground">Select additional products to include in this offer bundle.</p>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {products.filter(p => p.id !== form.offered_product_id).map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer" data-testid={`bundle-product-${p.id}`}>
                    <Checkbox checked={form.bundled_product_ids.includes(p.id)} onCheckedChange={() => toggleBundled(p.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.type} · ${p.price}</p>
                    </div>
                    {p.type === 'guide' ? <Headphones className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <Package className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                  </label>
                ))}
              </div>
              {form.bundled_product_ids.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-sm">
                  <span>{form.bundled_product_ids.length} product{form.bundled_product_ids.length > 1 ? 's' : ''} in bundle</span>
                  <span className="font-medium text-muted-foreground">Individual total: ${bundledTotal.toFixed(2)}</span>
                </div>
              )}
            </div>

            <Separator />

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#F0B429]" />Bundle Price ($)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="Set the offer price" data-testid="offer-price-input" />
                {bundledTotal > 0 && form.price && parseFloat(form.price) < bundledTotal && (
                  <p className="text-xs text-[#1A9DA5]">Customer saves ${(bundledTotal - parseFloat(form.price)).toFixed(2)} vs buying individually!</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Headline</Label>
                <Input value={form.headline} onChange={e => setForm({ ...form, headline: e.target.value })} placeholder="Special offer headline..." data-testid="offer-headline-input" />
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white" data-testid="save-offer-btn">
              {editingOffer ? 'Update Offer' : 'Create Offer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
